import React, { useEffect, useState } from "react";
import { GiShoppingCart } from "react-icons/gi";
import { ShopInfo } from "../shopinfo";
import { isQrEntry } from "../helper/isQrEntry";
import { useCart } from "../CartContext";
import { _get } from "../services/apiClient";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../helper/formatNumber";
import { useNavigate } from "react-router-dom";

function Footer() {
  const { itemQuantities } = useCart();
  // Count unique items with quantity > 0
  const totalQuantity = Object.values(itemQuantities).filter(
    (item) => item.quantity > 0,
  ).length;
  const [RestaurantInfo, setRestaurentInfo] = useState({});
  const year = new Date().getFullYear();
  const { t, i18n } = useTranslation();
  const shop = ShopInfo[i18n.language] || ShopInfo.en;
  const navigate = useNavigate();

  // State to track QR mode
  const [isQrMode, setIsQrMode] = useState(false);

  // Check QR mode on mount and when navigating
  useEffect(() => {
    const checkQrMode = () => {
      setIsQrMode(isQrEntry());
    };

    checkQrMode();

    // Listen for storage changes
    window.addEventListener("storage", checkQrMode);

    // Also check periodically in case localStorage changes in same tab
    const interval = setInterval(checkQrMode, 500);

    return () => {
      window.removeEventListener("storage", checkQrMode);
      clearInterval(interval);
    };
  }, []);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       const response = await _get(`/supplier/`);
  //       setRestaurentInfo(response.data.supplier);
  //     } catch (error) {
  //       console.error("Error fetching restaurant data:", error);
  //     }
  //   };
  //   fetchData();
  // }, []);

  return (
    <>
      {/* Mobile Bottom Navigation - Only visible on mobile */}
      <footer className="d-md-none d-block">
        <div
          className="fixed-bottom backdrop-blur-xl bg-white/95 shadow-lg border-t border-gray-200"
          style={{ zIndex: 999 }}
        >
          <div className="flex items-center justify-around px-4 py-3">
            <a
              href="/"
              className="flex flex-col items-center gap-1.5 text-gray-500 hover:text-gray-900 transition-all active:scale-95 flex-1"
            >
              <i className="feather-home text-2xl"></i>
              <span className="text-[11px] font-medium">{t("home")}</span>
            </a>

            <div className="flex-1 flex justify-center">
              <div
                className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-16 h-16 flex items-center justify-center cursor-pointer shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all relative -mt-8"
                onClick={() => {
                  if (isQrMode) {
                    navigate("/qr-cart");
                  } else {
                    navigate("/cart");
                  }
                }}
              >
                {totalQuantity > 0 && (
                  <span className="absolute -top-1.5 inset-inline-end-0 translate-x-1/2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center border-2 border-white shadow-lg">
                    {formatNumber(totalQuantity, i18n.language)}
                  </span>
                )}
                <i className="feather-shopping-cart text-white text-2xl"></i>
              </div>
            </div>

            <a
              href={`tel:${shop.Phone}`}
              className="flex flex-col items-center gap-1.5 text-gray-500 hover:text-gray-900 transition-all active:scale-95 flex-1"
            >
              <i className="feather-phone text-2xl"></i>
              <span className="text-[11px] font-medium">{t("call")}</span>
            </a>
          </div>
        </div>
      </footer>

      {/* Desktop Footer - Only visible on desktop */}
      <footer className="d-none d-md-block w-full bg-gradient-to-b from-white to-gray-50 border-t border-gray-200 mt-20">
        <div
          dir="ltr"
          className="w-full px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20 py-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {/* About Us Column */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <img
                  alt="Logo"
                  src={window.location.origin + "/img/logo black.png"}
                  className="h-16 w-auto"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">
                {shop.Name}
              </h3>
              <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">
                {t("aboutUs")}
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {RestaurantInfo?.aboutUs ||
                  t("welcomeMessage", { name: shop.Name })}
              </p>
            </div>

            {/* Contact Information Column */}
            <div>
              <h4 className="text-xs font-bold text-gray-500 mb-5 uppercase tracking-wider">
                {t("contactUs")}
              </h4>

              <div className="space-y-4">
                <div className="flex flex-row-reverse items-start gap-3 group">
                  <div className="bg-gray-800 rounded-xl p-3 group-hover:bg-gray-900 transition-colors shadow-sm">
                    <i className="feather-map-pin text-white text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-500 mb-1.5">
                      {t("location")}
                    </p>
                    <p className="text-sm text-gray-800 leading-relaxed">
                      {shop.Address}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row-reverse items-start gap-3 group">
                  <div className="bg-gray-800 rounded-xl p-3 group-hover:bg-gray-900 transition-colors shadow-sm">
                    <i className="feather-phone text-white text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-500 mb-1.5">
                      {t("phone")}
                    </p>
                    <a
                      href={`tel:${shop.Phone}`}
                      className="text-sm text-gray-800 hover:text-gray-900 transition-colors font-semibold"
                    >
                      {shop.Phone}
                    </a>
                  </div>
                </div>

                <div className="flex flex-row-reverse items-start gap-3 group">
                  <div className="bg-gray-800 rounded-xl p-3 group-hover:bg-gray-900 transition-colors shadow-sm">
                    <i className="feather-clock text-white text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-500 mb-1.5">
                      {t("hours")}
                    </p>
                    <p className="text-sm text-gray-800 font-medium">
                      {shop.OpenTime} - {shop.CloseTime}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links Column */}
            <div className="w-full">
              <h4 className="text-xs font-bold text-gray-500 mb-5 tracking-wider text-left">
                {t("quickLinks")}
              </h4>

              <div className="space-y-3 mb-6">
                {/* Home */}
                <a
                  href="/"
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                 text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all"
                >
                  <i className="feather-home text-gray-600 text-base shrink-0"></i>
                  <span className="font-medium">{t("home")}</span>
                </a>

                {/* Call */}
                <a
                  href={`tel:${shop.Phone}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                 text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all"
                >
                  <i className="feather-phone text-gray-600 text-base shrink-0"></i>
                  <span className="font-medium">{t("call")}</span>
                </a>

                {/* View Cart */}
                <button
                  onClick={() => {
                    if (isQrMode) {
                      navigate("/qr-cart");
                    } else {
                      navigate("/cart");
                    }
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                 text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all"
                >
                  <i className="feather-shopping-cart text-gray-600 text-base shrink-0"></i>
                  <span className="font-medium">{t("viewCart")}</span>
                </button>
              </div>

              {RestaurantInfo?.specialties && (
                <div className="p-5 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-lg">
                  <p className="flex items-center gap-2 mb-3 text-sm font-bold text-white">
                    <i className="feather-star text-yellow-400 shrink-0"></i>
                    {t("ourSpecialties")}
                  </p>
                  <p className="text-gray-300 text-xs leading-relaxed">
                    {RestaurantInfo.specialties}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="w-full border-t border-gray-200 bg-white py-6">
          <div className="w-full px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20">
            <div className="flex justify-center items-center text-xs text-gray-500">
              <p className="font-medium">
                © {year} {shop.Name}. {t("allRightsReserved")}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default Footer;
